import * as React from 'react';
import { CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface TagInputProps {
  value?: string[];
  onChange?: (tags: string[]) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  maxTags?: number;
  validate?: (tag: string) => boolean;
  className?: string;
  id?: string;
}

export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (
    { value = [], onChange, placeholder = 'Type and press Enter…', invalid, disabled, maxTags, validate, className, id },
    ref,
  ) => {
    const [draft, setDraft] = React.useState('');

    const addTag = (raw: string) => {
      const tag = raw.trim();
      if (!tag) return;
      if (value.includes(tag)) return;
      if (maxTags && value.length >= maxTags) return;
      if (validate && !validate(tag)) return;
      onChange?.([...value, tag]);
      setDraft('');
    };

    const removeTag = (idx: number) => {
      onChange?.(value.filter((_, i) => i !== idx));
    };

    return (
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] px-2 py-1',
          'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        {value.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--color-accent-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-accent)]"
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              disabled={disabled}
              onClick={() => removeTag(i)}
              className="hover:text-[var(--color-accent-hover)]"
            >
              <CloseLine size={12} />
            </button>
          </span>
        ))}
        <input
          ref={ref}
          id={id}
          type="text"
          disabled={disabled}
          value={draft}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addTag(draft);
            } else if (e.key === 'Backspace' && !draft && value.length > 0) {
              removeTag(value.length - 1);
            }
          }}
          onBlur={() => draft && addTag(draft)}
          className="min-w-[6rem] flex-1 bg-transparent text-sm text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-muted)]"
        />
      </div>
    );
  },
);
TagInput.displayName = 'TagInput';
